class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.addEventListener('close', () => {
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-closing');
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('dialog-open');
      if (this.opener?.isConnected) this.opener.focus();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-dialog-close]')) this.close();
    });
  }
  open(opener) {
    if (!this.dialog || this.dialog.open || this.dialog.classList.contains('is-closing')) return;
    this.opener = opener;
    document.documentElement.classList.add('dialog-open');
    this.dialog.showModal();
    requestAnimationFrame(() => this.querySelector('[data-dialog-initial-focus]')?.focus());
  }
  close() {
    if (!this.dialog?.open || this.dialog.classList.contains('is-closing')) return;
    const reduceMotion = document.documentElement.dataset.motion === 'none' || matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canAnimate = this.matches('.cart-modal,[data-animate-close]') && !reduceMotion;
    if (!canAnimate) {
      this.dialog.close();
      return;
    }
    this.dialog.classList.add('is-closing');
    const finish = (event) => {
      if (event && event.target !== this.dialog) return;
      this.dialog.removeEventListener('transitionend', finish);
      clearTimeout(this.closeTimer);
      if (this.dialog.open) this.dialog.close();
    };
    this.dialog.addEventListener('transitionend', finish);
    this.closeTimer = setTimeout(finish, 700);
  }
}
customElements.define('modal-dialog', ModalDialog);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-dialog-open]');
  if (!opener) return;
  const modal = document.getElementById(opener.getAttribute('aria-controls'));
  if (modal instanceof ModalDialog) { event.preventDefault(); modal.open(opener); }
});

class HeaderMenu extends HTMLElement {
  connectedCallback() {
    this.details = [...this.querySelectorAll('details')];
    this.openTimers = new Map();
    this.closeTimers = new Map();
    this.finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.details.forEach((item) => {
      const summary = item.querySelector('summary');
      summary?.addEventListener('click', () => {
        this.clearTimer(this.openTimers, item);
        this.clearTimer(this.closeTimers, item);
        this.closeOthers(item);
      });
      item.addEventListener('toggle', () => summary?.setAttribute('aria-expanded', String(item.open)));
      item.addEventListener('focusin', () => this.clearTimer(this.closeTimers, item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.scheduleClose(item);
      });
      if (this.finePointer) {
        item.addEventListener('mouseenter', () => this.scheduleOpen(item));
        item.addEventListener('mouseleave', () => this.scheduleClose(item));
      }
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openItem = this.querySelector('details[open]');
      if (openItem) {
        this.clearTimer(this.closeTimers, openItem);
        openItem.open = false;
        openItem.querySelector('summary')?.focus();
      }
    });
  }
  disconnectedCallback() {
    [...this.openTimers.values(), ...this.closeTimers.values()].forEach(clearTimeout);
    this.openTimers.clear();
    this.closeTimers.clear();
  }
  clearTimer(map, item) {
    clearTimeout(map.get(item));
    map.delete(item);
  }
  scheduleOpen(item) {
    this.clearTimer(this.closeTimers, item);
    this.clearTimer(this.openTimers, item);
    this.openTimers.set(item, setTimeout(() => {
      this.closeOthers(item);
      item.open = true;
      this.openTimers.delete(item);
    }, 120));
  }
  scheduleClose(item) {
    this.clearTimer(this.openTimers, item);
    this.clearTimer(this.closeTimers, item);
    this.closeTimers.set(item, setTimeout(() => {
      if (!item.matches(':focus-within')) item.open = false;
      this.closeTimers.delete(item);
    }, 180));
  }
  closeOthers(current) {
    this.details.forEach((item) => {
      if (item === current) return;
      this.clearTimer(this.openTimers, item);
      this.clearTimer(this.closeTimers, item);
      item.open = false;
    });
  }
}
customElements.define('header-menu', HeaderMenu);

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
    this.index = 0; this.paused = false;
    this.querySelector('[data-announcement-previous]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.querySelector('[data-announcement-pause]')?.addEventListener('click', (event) => { this.paused = !this.paused; event.currentTarget.setAttribute('aria-pressed', String(this.paused)); });
    if (this.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches && this.slides.length > 1) this.timer = setInterval(() => { if (!this.paused) this.show(this.index + 1); }, Number(this.dataset.interval));
  }
  disconnectedCallback() { clearInterval(this.timer); }
  show(index) { this.index = (index + this.slides.length) % this.slides.length; this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; }); }
}
customElements.define('announcement-rotator', AnnouncementRotator);

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    this.status = this.querySelector('[data-predictive-status]');
    this.error = this.querySelector('[data-predictive-error]');
    this.input?.addEventListener('input', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.search(), 250); });
  }
  disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); }
  clear() {
    if (this.results) this.results.innerHTML = '';
    if (this.status) this.status.textContent = '';
    if (this.error) { this.error.textContent = ''; this.error.hidden = true; }
    this.input?.setAttribute('aria-expanded', 'false');
  }
  async search() {
    const query = this.input?.value.trim() || '';
    if (query.length < 2) {
      this.controller?.abort();
      this.controller = null;
      this.clear();
      this.removeAttribute('aria-busy');
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.clear();
    this.setAttribute('aria-busy', 'true');
    try {
      const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(response.status);
      const html = await response.text();
      if (this.controller !== controller) return;
      this.results.innerHTML = html;
      const message = this.results.querySelector('[data-predictive-message]');
      if (this.status) this.status.textContent = message?.textContent.trim() || '';
      this.input.setAttribute('aria-expanded', String(Boolean(this.results.textContent.trim())));
    } catch (error) {
      if (error.name !== 'AbortError' && this.controller === controller && this.error) {
        this.error.textContent = this.dataset.error || '';
        this.error.hidden = false;
      }
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.removeAttribute('aria-busy');
      }
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

class LocalizationForm extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-localization-submit]').forEach((button) => { button.hidden = true; });
    this.querySelectorAll('select').forEach((select) => select.addEventListener('change', () => this.querySelector('form')?.requestSubmit()));
  }
}
customElements.define('localization-form', LocalizationForm);


class ThemeMotionController {
  constructor() {
    this.root = document.documentElement;
    this.reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    if (this.root.dataset.motion === 'none' || this.reduceMotion.matches || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        this.observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    this.collect(document);
    if (this.root.dataset.motion === 'expressive' && matchMedia('(hover: hover) and (pointer: fine)').matches) this.attachTilts(document);
    this.root.classList.add('motion-ready');
    document.addEventListener('shopify:section:load', (event) => {
      this.collect(event.target);
      if (this.root.dataset.motion === 'expressive') this.attachTilts(event.target);
    });
  }

  collect(scope) {
    scope.querySelectorAll('[data-reveal]').forEach((element) => this.prepare(element, element.dataset.reveal));
    const groups = [
      ['.page-hero:not(.page-hero--motion-none) .page-hero__copy, .collection-hero__copy, .collection-list>header, .product-page__info, .blog-index__header, .article-page__head, .main-search__header, .password-page__content, .not-found__copy', 'up'],
      ['.page-hero:not(.page-hero--motion-none) .page-hero__media, .collection-hero__media, .product-gallery__item.is-active, .article-page__media, .password-page__media, .not-found__finder', 'media'],
      ['.collection-hero__stamp, .blog-index__featured, .article-page__main, .main-search__meta, .search-empty', 'up'],
      ['.signature-heading, .story-lab__content, .object-study__copy, .journal-signup__journal > header, .journal-signup__signup, .contact-panel__intro, .contact-panel__form', 'up'],
      ['.story-lab__media, .lookbook-shop__stage, .object-study__media', 'media']
    ];
    groups.forEach(([selector, variant]) => scope.querySelectorAll(selector).forEach((element) => this.prepare(element, variant)));

    const repeated = [
      '.mood-card', '.product-card', '.campaign-frame', '.story-lab__card',
      '.proof-card', '.service-card', '.faq-row', '.journal-card', '.article-card',
      '.search-grid > .content-card', '.collection-promo', '.contact-panel__details > div'
    ];
    repeated.forEach((selector) => scope.querySelectorAll(selector).forEach((element, index) => {
      this.prepare(element, element.matches('.campaign-frame, .mood-card, .article-card, .journal-card') ? 'media' : 'up', Math.min(index, 3));
    }));
  }

  prepare(element, variant, step = 0) {
    if (!element.hasAttribute('data-reveal')) element.dataset.reveal = variant;
    if (!element.style.getPropertyValue('--reveal-delay')) element.style.setProperty('--reveal-delay', `calc(var(--motion-stagger) * ${step})`);
    if (element.classList.contains('is-visible')) return;
    this.observer.observe(element);
  }

  attachTilts(scope) {
    scope.querySelectorAll('[data-motion-tilt]').forEach((element) => {
      if (element.dataset.motionTiltReady === 'true') return;
      element.dataset.motionTiltReady = 'true';
      let bounds;
      let frame;
      let point;
      const render = () => {
        frame = 0;
        if (!bounds || !point) return;
        const x = (point.clientX - bounds.left) / bounds.width - 0.5;
        const y = (point.clientY - bounds.top) / bounds.height - 0.5;
        element.style.setProperty('--motion-tilt-x', `${(-y * 2.4).toFixed(2)}deg`);
        element.style.setProperty('--motion-tilt-y', `${(x * 3.2).toFixed(2)}deg`);
      };
      element.addEventListener('pointerenter', () => { bounds = element.getBoundingClientRect(); });
      element.addEventListener('pointermove', (event) => {
        point = event;
        if (!frame) frame = requestAnimationFrame(render);
      });
      element.addEventListener('pointerleave', () => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        bounds = null;
        point = null;
        element.style.setProperty('--motion-tilt-x', '0deg');
        element.style.setProperty('--motion-tilt-y', '0deg');
      });
    });
  }
}

new ThemeMotionController();
